import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { OnboardingLayout } from "./OnboardingLayout";
import { saveOnboarding } from "../../api/onboarding";
import { ApiError } from "../../api/client";

/**
 * Screen 4 — two equal buttons, deliberately.
 *
 * Neither answer is styled as the preferred one. An under-18 who feels nudged
 * will simply lie, and the consent gate exists precisely because that answer
 * has to be true. The footer says so out loud.
 */
export function AgeCheckScreen() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * The answer is recorded before moving on.
   *
   * It used to navigate and nothing else, so the one fact the consent gate
   * depends on was thrown away — HR-3 makes this answer the whole reason a
   * minor needs a parent, and a student arrived at the waiting room with
   * nothing on record saying they were under 18.
   */
  const answer = async (isUnderAge: boolean) => {
    if (saving) return;
    setSaving(true);
    setError(null);

    try {
      await saveOnboarding({ isUnderAge });
      void navigate(isUnderAge ? "/parent-phone" : "/exam");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't save that. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingLayout
      step={3}
      title="Are you under 18?"
      subtitle="We ask because students under 18 need a parent's OK — it takes one text."
      action={
        <div className="flex flex-col gap-3">
          <Button variant="secondary" disabled={saving} onClick={() => void answer(true)}>
            Yes, I'm under 18
          </Button>
          <Button variant="secondary" disabled={saving} onClick={() => void answer(false)}>
            No, I'm 18 or older
          </Button>
          {error ? (
            <p role="alert" className="m-0 text-center text-[12.5px] text-negative">
              {error}
            </p>
          ) : null}
        </div>
      }
      footer={
        <p className="m-0 text-center text-[12.5px] leading-[1.55] text-text-muted">
          Honest answer, please — it decides one small step, nothing else. Your practice works the
          same either way.
        </p>
      }
    >
      <span />
    </OnboardingLayout>
  );
}
